import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dumbbell, Check, Trophy, Timer, Plus, X,
  Trash2, GripVertical, Share2, Camera, Weight,
  Globe, Lock, Loader2, Users, ChevronDown, ChevronUp, Images, Send,
} from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useRestTimer } from '../../contexts/RestTimerContext';
import { useWorkoutMeta } from '../../contexts/WorkoutMetaContext';
import { useWorkoutData, useWorkoutDataActions } from '../../contexts/WorkoutDataContext';
import { useWorkoutProgress } from '../../contexts/WorkoutProgressContext';
import client from '../../api/client';
import { translateMuscle } from '../../lib/muscleTranslations';
import { exerciseName, isTimeBasedExercise, defaultSetCount } from '../../lib/exerciseDisplay';
import { TARGET_MUSCLE_COLORS } from '../../lib/muscleUtils';
import { haptics } from '../../utils/haptics';
import { getExerciseKey } from '../../utils/exerciseKey';
import { useRoutableDialog } from '../../hooks/useRoutableDialog';

import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';

import LazyImage from '../LazyImage';
import ImageCropper from '../ImageCropper';
import ExercisePicker from '../ExercisePicker';
import StaticExerciseImage from '../StaticExerciseImage';
import ExerciseDetailModal from '../ExerciseDetailModal';
import RoutineShareMenu from '../RoutineShareMenu';
import MiniMuscleMap from '../MiniMuscleMap';
import { showPrCelebration } from '../PRCelebration';
import PrCardGenerator from '../PrCardGenerator';
import PrPickerSheet from '../PrPickerSheet';
import WorkoutStoryGenerator from '../WorkoutStoryGenerator';
import WarmUpCalculator from '../WarmUpCalculator';
import TechniqueCueBubbleInline from '../TechniqueCueBubbleInline';
import { techniqueCuesApi } from '../../api/techniqueCues';
import MilestoneCardGenerator from '../MilestoneCardGenerator';
import { showBadgeCelebration } from '../BadgeCelebration';
import {
  startWorkoutNotification,
  updateWorkoutNotification,
  stopWorkoutNotification,
} from '../../services/workoutNotificationService';
import { TA } from '../tour/tourAnchors';
import { useTourEvent } from '../tour/tourEventBus';
import {
  getMatchingPreviousSet,
  createWorkoutExerciseId,
  createSetKey,
  ensureWorkoutExerciseIds,
  buildReorderIndexMap,
  remapIndexedRecord,
  remapCompletedSets,
  remapAppliedProgression,
  clearWorkoutStorage,
  isBodyweightExercise,
  computeUnrealisticFlags,
  PLATE_COLORS,
  calculatePlates,
} from './liveSessionUtils';
import ExerciseRow from './ExerciseRow';
import WorkoutTimer from './WorkoutTimer';

/* SET_TYPES + SetTypePopover live in ./sharedSetTypes (v3.8.178 cleanup —
   moved out to break the SetRow → LiveSession → ExerciseRow → SetRow
   import cycle). LiveSession no longer needs to import them directly. */

/* ── Plate Calculator ────────────────────────────────────────── */
function PlateCalculator({ weight, onClose }) {
  const { t } = useTranslation();
  const [barWeight, setBarWeight] = useState(20);
  const result = calculatePlates(weight || 0, barWeight);

  return (
    <div className="p-3 space-y-3 min-w-[200px]" data-testid="plate-calculator">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t('workout.plateCalculator')}</span>
        <div className="flex items-center gap-1">
          {[0, 10, 15, 20].map(bw => (
            <button
              key={bw}
              onClick={() => setBarWeight(bw)}
              className={`px-2 py-0.5 rounded text-xs font-bold transition-colors ${barWeight === bw ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
            >
              {bw}kg
            </button>
          ))}
        </div>
      </div>

      {weight >= barWeight && weight > 0 ? (
        <>
          <div className="text-center">
            <span className="text-lg font-bold">{result.perSide}kg</span>
            <span className="text-xs text-muted-foreground ml-1">{t('workout.perSide')}</span>
          </div>

          {/* Visual bar representation */}
          <div className="flex items-center justify-center gap-0.5 py-2">
            {/* Left plates */}
            <div className="flex items-center gap-0.5">
              {[...result.plates].reverse().map((p, i) => (
                <div key={`l-${i}`} className="rounded-sm flex items-center justify-center text-xs font-bold border border-border/30"
                  style={{
                    width: `${Math.max(12, p * 1.2)}px`,
                    height: `${Math.max(24, p * 1.5)}px`,
                    backgroundColor: PLATE_COLORS[p] || '#94a3b8',
                    color: p >= 10 ? '#fff' : '#000'
                  }}>
                  {p >= 5 ? p : ''}
                </div>
              ))}
            </div>
            {/* Bar */}
            <div className="w-16 h-3 bg-neutral-400 dark:bg-neutral-500 rounded-full" />
            {/* Right plates */}
            <div className="flex items-center gap-0.5">
              {result.plates.map((p, i) => (
                <div key={`r-${i}`} className="rounded-sm flex items-center justify-center text-xs font-bold border border-border/30"
                  style={{
                    width: `${Math.max(12, p * 1.2)}px`,
                    height: `${Math.max(24, p * 1.5)}px`,
                    backgroundColor: PLATE_COLORS[p] || '#94a3b8',
                    color: p >= 10 ? '#fff' : '#000'
                  }}>
                  {p >= 5 ? p : ''}
                </div>
              ))}
            </div>
          </div>

          {/* Plate list */}
          <div className="flex flex-wrap gap-1 justify-center">
            {result.plates.map((p, i) => (
              <span key={i} className="px-1.5 py-0.5 rounded text-xs font-mono font-bold bg-muted">{p}kg</span>
            ))}
          </div>
          {result.remainder > 0 && (
            <p className="text-xs text-amber-500 text-center">{t('workout.cannotLoadPlates', { remainder: result.remainder })}</p>
          )}
        </>
      ) : (
        <p className="text-xs text-muted-foreground text-center py-2">
          {weight ? t('workout.weightExceedsBar', { bar: barWeight }) : t('workout.enterWeightToCalc')}
        </p>
      )}
    </div>
  );
}

/* ── Sortable Exercise Item for Reorder Mode ─────────────────── */
function SortableExerciseItem({ id, exercise, index, onMoveUp, onMoveDown, isFirst, isLast }) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  const imgSrc = exercise.gifUrl || exercise.image_url || '';

  return (
    <div ref={setNodeRef} style={style}
      className={`flex items-center gap-2 p-3 rounded-xl border transition-all ${
        isDragging ? 'bg-primary/10 border-primary/30 shadow-lg' : 'bg-card border-border/50 hover:border-primary/50'
      }`}
      data-testid={`reorder-item-${index}`}
    >
      {/* Drag handle */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            ref={setActivatorNodeRef}
            type="button"
            {...attributes}
            {...listeners}
            style={{ touchAction: 'none' }}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors cursor-grab active:cursor-grabbing hover:bg-muted active:bg-muted hover:text-foreground"
            aria-label={`Drag to reorder ${exercise.name}`}
          >
            <GripVertical className="h-5 w-5" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{t('workout.dragToReorder')}</TooltipContent>
      </Tooltip>
      <div className="relative w-8 h-8 rounded-lg overflow-hidden bg-muted flex-shrink-0">
        <StaticExerciseImage src={imgSrc} alt={exercise.name} size="sm" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {(exerciseName(exercise, language) || '').toUpperCase()}
        </p>
        <p className="text-xs text-muted-foreground capitalize">{(exercise.targetMuscles || []).map(m => translateMuscle(m, language)).join(', ') || translateMuscle(exercise.muscle_group, language)} · {exercise.sets.length} {t('workout.setsLabel')}</p>
      </div>
      {/* Up/Down arrow buttons */}
      <div className="flex flex-col gap-0.5 flex-shrink-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onMoveUp}
              disabled={isFirst}
              className="flex h-11 w-11 md:h-7 md:w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted active:bg-muted hover:text-foreground disabled:opacity-25 disabled:cursor-not-allowed"
              aria-label={`Move ${exercise.name} up`}
            >
              <ChevronUp className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>{t('workout.moveUp')}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onMoveDown}
              disabled={isLast}
              className="flex h-11 w-11 md:h-7 md:w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted active:bg-muted hover:text-foreground disabled:opacity-25 disabled:cursor-not-allowed"
              aria-label={`Move ${exercise.name} down`}
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>{t('workout.moveDown')}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

/* ── Exercise Thumbnail ──────────────────────────────────────── */
// Must be defined OUTSIDE the consuming component so React doesn't remount it on every render.
export function ExerciseThumb({ name, gifUrl: directUrl, exerciseImages = {} }) {
  const url = directUrl || exerciseImages[name];
  if (url) {
    return <StaticExerciseImage src={url} alt={name} className="w-full h-full object-cover rounded" />;
  }
  return <Dumbbell className="h-5 w-5 text-muted-foreground" />;
}

/* WorkoutTimer moved to ./WorkoutTimer.js (v3.8.170 refactor). */

/* ════════════════════════════════════════════════════════════════
   <LiveSession />

   Owns ALL in-progress-workout UI: the sticky top bar, the
   exercise list, set rows, the finish button, the incomplete-sets
   banner, AND the post-workout completion dialog flow. Reads from
   four contexts (meta / data / data-actions / progress) — takes
   no props.

   Returns null when there's nothing to show (no active workout
   AND no completion dialog open). When a workout is in progress,
   renders the active-workout view AND the completion dialog
   shell (the dialog opens itself when `showCompletionDialog`
   flips true). When the user is on the routine picker but the
   completion dialog is mid-save, the dialog shell still renders
   so the modal can stay open across the activeWorkout=null
   transition.
   ════════════════════════════════════════════════════════════════ */
export default function LiveSession() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { restTimer, restTimerRunning, startRestTimer, skipRestTimer } = useRestTimer();

  // ── Provider-owned session state ────────────────────────────
  const {
    activeWorkout, setActiveWorkout,
    sourcePersonalRoutineId, setSourcePersonalRoutineId,
    sourcePersonalDayId, setSourcePersonalDayId,
    onBehalfOf, setOnBehalfOf,
    clientName, setClientName,
    workoutSessionKey,
    startedAt, setStartedAt,
    workoutPrefs,
  } = useWorkoutMeta();

  const { workoutData, lastWeights, appliedProgression } = useWorkoutData();
  const { setWorkoutData, setLastWeights, setAppliedProgression } = useWorkoutDataActions();

  const {
    completedSets, setCompletedSets,
    incompleteSets, setIncompleteSets,
    showIncompleteAlert, setShowIncompleteAlert,
    sessionPrMaxRef,
    sessionPrRepsRef,
    isTimerRunning, setIsTimerRunning,
    completedWorkout, setCompletedWorkout,
    showCompletionDialog, setShowCompletionDialog,
    setShowUpdateRoutinePrompt,
  } = useWorkoutProgress();

  // ── Live-session-local UI state ─────────────────────────────
  const wakeLockRef = useRef(null);
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [exerciseResults, setExerciseResults] = useState([]);
  const [swapExerciseIndex, setSwapExerciseIndex] = useState(null);
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [swapSearch, setSwapSearch] = useState('');
  const [swapResults, setSwapResults] = useState([]);
  const [swapMuscleFilter, setSwapMuscleFilter] = useState('');
  const [exerciseDetail, setExerciseDetail] = useState(null);
  const [showExerciseDetail, setShowExerciseDetail] = useState(false);
  const [prCardPr, setPrCardPr] = useState(null); // { exerciseName, weight, reps, prType, prevBest } | null
  const [prPickerOpen, setPrPickerOpen] = useState(false);
  const [prPickerList, setPrPickerList] = useState([]);
  const exerciseDialog = useRoutableDialog('exercise');
  // URL → state sync for exercise detail
  useEffect(() => {
    if (!exerciseDialog.isOpen) {
      if (showExerciseDetail) setShowExerciseDetail(false);
    }
  }, [exerciseDialog.isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  const [allExercisesData, setAllExercisesData] = useState({});
  const [removeExerciseIndex, setRemoveExerciseIndex] = useState(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const [exerciseContextMenu, setExerciseContextMenu] = useState({ open: false, exIdx: null });
  const [setContextMenu, setSetContextMenu] = useState({ open: false, exIdx: null, setIdx: null });
  const [plateCalcDialog, setPlateCalcDialog] = useState({ open: false, weight: 0, exerciseName: '' });
  const [warmupDialog, setWarmupDialog] = useState({ open: false, weight: 0, reps: 5, exercise: null });
  const [unrealisticConfirmDialog, setUnrealisticConfirmDialog] = useState({
    open: false,
    exerciseIndex: null,
    setIndex: null,
    exerciseName: '',
    reps: 0,
    weight: 0,
    flags: { reps: false, weight: false },
  });
  const [shareBadgeDialog, setShareBadgeDialog] = useState({ open: false, badge: null });
  const setLongPressRef = useRef({ timer: null, triggered: false, startX: 0, startY: 0 });
  const reorderLongPressRef = useRef({ timer: null, triggered: false, startX: 0, startY: 0 });
  const reorderSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 5 } })
  );

  const clearReorderLongPress = useCallback(() => {
    if (reorderLongPressRef.current.timer) {
      clearTimeout(reorderLongPressRef.current.timer);
      reorderLongPressRef.current.timer = null;
    }
  }, []);

  const beginReorderLongPress = useCallback((event, exIdx) => {
    if (reorderMode) return;
    if (event.button !== undefined && event.button !== 0) return;

    clearReorderLongPress();
    reorderLongPressRef.current.triggered = false;
    reorderLongPressRef.current.startX = event.clientX ?? 0;
    reorderLongPressRef.current.startY = event.clientY ?? 0;
    reorderLongPressRef.current.timer = setTimeout(() => {
      reorderLongPressRef.current.triggered = true;
      reorderLongPressRef.current.timer = null;
      setExerciseContextMenu({ open: true, exIdx });
    }, 450);
  }, [clearReorderLongPress, reorderMode]);

  const moveReorderLongPress = useCallback((event) => {
    if (!reorderLongPressRef.current.timer) return;

    const movedX = Math.abs((event.clientX ?? 0) - reorderLongPressRef.current.startX);
    const movedY = Math.abs((event.clientY ?? 0) - reorderLongPressRef.current.startY);

    if (movedX > 8 || movedY > 8) {
      clearReorderLongPress();
    }
  }, [clearReorderLongPress]);

  const endReorderLongPress = useCallback(() => {
    clearReorderLongPress();
  }, [clearReorderLongPress]);

  const clearSetLongPress = useCallback(() => {
    if (setLongPressRef.current.timer) {
      clearTimeout(setLongPressRef.current.timer);
      setLongPressRef.current.timer = null;
    }
  }, []);

  const beginSetLongPress = useCallback((event, exIdx, setIdx) => {
    if (event.button !== undefined && event.button !== 0) return;
    // Don't start timer if the user is aiming at an input/button inside the row
    const tag = event.target?.tagName;
    if (tag === 'INPUT' || tag === 'BUTTON' || tag === 'SVG' || tag === 'PATH') return;
    if (event.target?.closest?.('input, button, [role="button"]')) return;

    clearSetLongPress();
    setLongPressRef.current.triggered = false;
    setLongPressRef.current.startX = event.clientX ?? 0;
    setLongPressRef.current.startY = event.clientY ?? 0;
    setLongPressRef.current.timer = setTimeout(() => {
      setLongPressRef.current.triggered = true;
      setLongPressRef.current.timer = null;
      if (document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
      setSetContextMenu({ open: true, exIdx, setIdx });
    }, 450);
  }, [clearSetLongPress]);

  const moveSetLongPress = useCallback((event) => {
    if (!setLongPressRef.current.timer) return;
    const movedX = Math.abs((event.clientX ?? 0) - setLongPressRef.current.startX);
    const movedY = Math.abs((event.clientY ?? 0) - setLongPressRef.current.startY);
    if (movedX > 8 || movedY > 8) clearSetLongPress();
  }, [clearSetLongPress]);

  const endSetLongPress = useCallback(() => {
    clearSetLongPress();
  }, [clearSetLongPress]);


  const markAllSetsDone = useCallback((exIdx) => {
    const exercise = workoutData[exIdx];
    if (!exercise) return;
    const updates = {};
    (exercise.sets || []).forEach((_, sIdx) => { updates[`${exIdx}-${sIdx}`] = true; });
    setCompletedSets(prev => ({ ...prev, ...updates }));
  }, [workoutData, setCompletedSets]);

  const focusExerciseNote = useCallback((exIdx) => {
    const input = document.querySelector(`[data-testid="session-note-input-${exIdx}"]`);
    if (input) { input.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => input.focus(), 300); }
  }, []);

  useEffect(() => {
    return () => clearReorderLongPress();
  }, [clearReorderLongPress]);

  useEffect(() => {
    return () => clearSetLongPress();
  }, [clearSetLongPress]);

  useEffect(() => {
    if (!reorderMode) {
      reorderLongPressRef.current.triggered = false;
    }
  }, [reorderMode]);

  // ── Completion Dialog State ─────────────────────────────────
  const [completionPhoto, setCompletionPhoto] = useState(null);
  const [completionTab, setCompletionTab] = useState('summary');
  const [completionDescription, setCompletionDescription] = useState('');
  const [completionVisibility, setCompletionVisibility] = useState('public');
  const [editDuration, setEditDuration] = useState(null);
  const [workoutDate, setWorkoutDate] = useState(null); // null = now, or ISO string for past workouts
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showSaveAsRoutine, setShowSaveAsRoutine] = useState(false);
  const [saveRoutineChoice, setSaveRoutineChoice] = useState('new'); // 'new' or 'existing'
  const [saveRoutineTarget, setSaveRoutineTarget] = useState('');
  const [saveRoutineName, setSaveRoutineName] = useState('');
  const [saveRoutineDayName, setSaveRoutineDayName] = useState('');
  const [showCompletionStory, setShowCompletionStory] = useState(false);
  // Image cropper state for workout completion photo
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropperSrc, setCropperSrc] = useState(null);
  // Ref mirrors cropperOpen so the completion dialog's onOpenChange always
  // reads the latest value without stale-closure issues.
  // Also covers the file-picker phase: we set it true before the picker opens
  // so that any popstate fired by the native file picker doesn't close the
  // completion dialog (the onOpenChange guard checks this ref).
  const cropperActiveRef = useRef(false);

  // ── Session UI State (per exercise, session-specific) ───────
  const [sessionNotes, setSessionNotes] = useState({});
  const [expandedNotes, setExpandedNotes] = useState({}); // eslint-disable-line no-unused-vars
  const [supersetGroups, setSupersetGroups] = useState({}); // { exerciseIndex: groupId }
  const [nextSupersetGroup, setNextSupersetGroup] = useState(1);
  const [defaultRestSeconds, setDefaultRestSeconds] = useState({}); // per exercise index

  // Hydrate sessionNotes from workoutData when the WorkoutSessionProvider's
  // localStorage rehydration completes. Notes typed in a prior mount are
  // mirrored into workoutData[i].session_note by handleSessionNoteChange (the
  // separate live-only field — `notes` stays as the coach cue). On remount
  // sessionNotes is reset to {} and this effect copies them back so the typed
  // note doesn't disappear when the user navigates away from /workout
  // mid-session and comes back.
  const hydratedNotesRef = useRef(false);
  useEffect(() => {
    if (hydratedNotesRef.current || !workoutData?.length) return;
    const fromWorkout = {};
    workoutData.forEach((ex, i) => { if (ex?.session_note) fromWorkout[i] = ex.session_note; });
    if (Object.keys(fromWorkout).length > 0) {
      setSessionNotes(prev => ({ ...fromWorkout, ...prev })); // prev wins so in-progress typing isn't clobbered
    }
    hydratedNotesRef.current = true;
  }, [workoutData]);

  /* ── React Query: server data needed for live session ──────── */
  const { data: assignedRoutine = null } = useQuery({
    queryKey: ['assignedRoutine', onBehalfOf || null],
    queryFn: async () => {
      const url = onBehalfOf ? `/users/${onBehalfOf}/routine` : `/users/me/routine`;
      const response = await client.get(url);
      return response.data || null;
    },
    staleTime: 10 * 60 * 1000, // routine rarely changes mid-session
  });

  const { data: personalRoutines = [] } = useQuery({
    queryKey: ['personalRoutines'],
    queryFn: async () => {
      const response = await client.get('/routines/personal');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
  });

  const { data: exerciseImages = {} } = useQuery({
    queryKey: ['exerciseImageMap'],
    queryFn: async () => {
      const res = await client.get('/exercises/image-map');
      return res.data;
    },
    staleTime: 30 * 60 * 1000,
  });

  // Batch-resolve technique cues for all exercises in the active workout
  // to avoid N+1 individual /resolve calls.
  const exerciseIds = useMemo(
    () => (workoutData || []).map(getExerciseKey).filter(Boolean),
    [workoutData],
  );

  const { data: cuesMap = {} } = useQuery({
    queryKey: ['technique-cues-batch', exerciseIds.slice().sort().join(',')],
    queryFn: async () => (await techniqueCuesApi.resolveBatch(exerciseIds)).data,
    enabled: exerciseIds.length > 0,
    staleTime: 60_000,
  });

  /* ── Progression step config (per exercise, persisted to backend) ── */
  const [progressionSteps, setProgressionSteps] = useState(() => {
    try { return JSON.parse(localStorage.getItem('trainedbyrs_progression_steps') || '{}'); } catch { return {}; }
  });

  // Load from backend on mount and merge with localStorage cache
  useEffect(() => {
    client.get('/preferences/progression-steps').then(res => {
      const remote = res.data || {};
      if (Object.keys(remote).length > 0) {
        setProgressionSteps(prev => {
          const merged = { ...prev, ...remote };
          localStorage.setItem('trainedbyrs_progression_steps', JSON.stringify(merged));
          return merged;
        });
      }
    }).catch(() => {});
  }, []);

  const getProgressionStep = (exerciseName) => progressionSteps[exerciseName] || workoutPrefs.autoProgressStep;

  const updateProgressionStep = (exerciseName, value) => {
    const step = Math.max(0.5, parseFloat(value) || workoutPrefs.autoProgressStep);
    const updated = { ...progressionSteps, [exerciseName]: step };
    setProgressionSteps(updated);
    localStorage.setItem('trainedbyrs_progression_steps', JSON.stringify(updated));
    // Persist to backend
    client.put(`/preferences/progression-steps/${encodeURIComponent(exerciseName)}?step=${step}`).catch(() => {});
  };

  const applyProgression = (exerciseIndex, direction) => {
    const exercise = workoutData[exerciseIndex];
    const step = progressionSteps[exercise.name] ||
      (direction === 'increase' ? workoutPrefs.autoProgressStep : workoutPrefs.autoDeloadStep);
    const delta = direction === 'increase' ? step : -step;

    // Only apply to WORK SETS, not warmup/drop/backoff
    setWorkoutData(prev => prev.map((ex, i) =>
      i === exerciseIndex ? {
        ...ex,
        sets: ex.sets.map(s =>
          s.set_type === 'work' || !s.set_type
            ? { ...s, weight: Math.max(0, Math.round(((s.weight || 0) + delta) * 10) / 10) }
            : s
        )
      } : ex
    ));
    // Track applied progression using workout session key (so it persists across tab changes)
    setAppliedProgression(prev => new Map([...prev, [`${workoutSessionKey}-${exerciseIndex}`, direction]]));
    toast.success(t('workout.progressionApplied', { sign: direction === 'increase' ? '+' : '-', step, name: exercise.name }));
  };

  const removeExercise = (exerciseIndex) => {
    const name = workoutData[exerciseIndex]?.name;
    setWorkoutData(prev => prev.filter((_, i) => i !== exerciseIndex));
    setCompletedSets(prev => {
      const n = {};
      Object.keys(prev).forEach(k => {
        const [ei, si] = k.split('-').map(Number);
        if (ei < exerciseIndex) n[k] = true;
        else if (ei > exerciseIndex) n[`${ei - 1}-${si}`] = true;
      });
      return n;
    });
    setShowRemoveConfirm(false);
    setRemoveExerciseIndex(null);
    toast.success(t('workout.exerciseRemoved', { name }));
  };

  /* ── Wake Lock: keep screen on during active workout ───────── */
  useEffect(() => {
    if (workoutPrefs.keepScreenOn && activeWorkout && 'wakeLock' in navigator) {
      navigator.wakeLock.request('screen').then(lock => { wakeLockRef.current = lock; }).catch(() => {});
    } else if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
    return () => { if (wakeLockRef.current) { wakeLockRef.current.release().catch(() => {}); } };
  }, [workoutPrefs.keepScreenOn, activeWorkout]);

  // Build workout info snapshot for notification service
  const buildInfo = useCallback(() => {
    const exIdx = workoutData.findIndex((ex, i) =>
      (ex.sets || []).some((_, si) => !completedSets[`${i}-${si}`])
    );
    const safeIdx = exIdx === -1 ? Math.max(0, workoutData.length - 1) : exIdx;
    const exercise = workoutData[safeIdx];
    const completedCount = exercise
      ? (exercise.sets || []).filter((_, si) => completedSets[`${safeIdx}-${si}`]).length
      : 0;
    return {
      exerciseName: exercise?.name || 'Workout',
      setNumber: completedCount + 1,
      totalSets: exercise?.sets?.length || 1,
      isResting: restTimerRunning,
      restSecondsLeft: restTimer,
    };
  }, [workoutData, completedSets, restTimerRunning, restTimer]);

  /* ── Workout Notification: start/stop ───────────────────────── */
  useEffect(() => {
    if (activeWorkout) {
      startWorkoutNotification(buildInfo());
    } else {
      stopWorkoutNotification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkout]);

  /* ── Workout Notification: exercise or set change ────────── */
  useEffect(() => {
    if (activeWorkout) {
      updateWorkoutNotification(buildInfo());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutData, completedSets]);

  /* ── Workout Notification: rest timer start/stop ─────────── */
  useEffect(() => {
    if (activeWorkout) {
      updateWorkoutNotification(buildInfo());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restTimerRunning]);

  /* ── Tour: emit first-set-completed event ─────────────────── */
  const firstSetCompleted = useMemo(() => {
    return completedSets['0-0'] === true ? true : undefined;
  }, [completedSets]);
  useTourEvent('workout.first-set-completed', firstSetCompleted);

  // Superset helpers
  const toggleSuperset = (exIdx) => {
    setSupersetGroups(prev => {
      const copy = { ...prev };
      if (copy[exIdx] !== undefined) {
        // Remove from superset
        const groupId = copy[exIdx];
        delete copy[exIdx];
        // If only one exercise remains in the group, remove it too
        const remaining = Object.entries(copy).filter(([, g]) => g === groupId);
        if (remaining.length === 1) delete copy[remaining[0][0]];
        return copy;
      }
      // Link with next exercise
      if (exIdx < workoutData.length - 1) {
        const nextIdx = exIdx + 1;
        // Check if next exercise is already in a group
        if (copy[nextIdx] !== undefined) {
          copy[exIdx] = copy[nextIdx];
        } else {
          const groupId = nextSupersetGroup;
          setNextSupersetGroup(g => g + 1);
          copy[exIdx] = groupId;
          copy[nextIdx] = groupId;
        }
      }
      return copy;
    });
  };

  const getSupersetColor = (groupId) => {
    const colors = ['border-l-cyan-500', 'border-l-violet-500', 'border-l-amber-500', 'border-l-emerald-500', 'border-l-rose-500'];
    return colors[(groupId - 1) % colors.length];
  };

  const getSupersetBgColor = (groupId) => {
    const colors = ['bg-cyan-500', 'bg-violet-500', 'bg-amber-500', 'bg-emerald-500', 'bg-rose-500'];
    return colors[(groupId - 1) % colors.length];
  };

  const saveWorkoutAsRoutineMutation = useMutation({
    mutationFn: async (payload) => {
      await client.post('/routines/personal/save-workout', payload);
    },
    onSuccess: () => {
      toast.success(t('workout.savedToRoutines'));
      setShowSaveAsRoutine(false);
      queryClient.invalidateQueries({ queryKey: ['personalRoutines'] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || t('workout.failedSaveRoutine'));
    },
  });

  const savingRoutine = saveWorkoutAsRoutineMutation.isPending;

  const saveWorkoutAsRoutine = () => {
    if (!completedWorkout) return;
    const exercises = (completedWorkout.exercises_detail || completedWorkout.exercises_raw || []).map(ex => ({
      name: ex.name,
      muscle_group: ex.muscle_group,
      targetMuscles: ex.targetMuscles || [],
      sets: (ex.sets || []).map(s => ({
        set_type: s.type || s.set_type || 'work',
        target_reps_min: s.reps || s.target_reps_min || s.completed_reps || 8,
        target_reps_max: s.reps || s.target_reps_max || s.completed_reps || 12,
      }))
    }));
    const payload = {
      day_name: saveRoutineDayName || completedWorkout.day_name || 'Workout',
      exercises,
    };
    if (saveRoutineChoice === 'existing' && saveRoutineTarget) {
      payload.routine_id = saveRoutineTarget;
    } else {
      payload.new_routine_name = saveRoutineName || 'My Routine';
    }
    saveWorkoutAsRoutineMutation.mutate(payload);
  };

  const fetchLastWeights = async (exerciseName) => {
    try {
      const response = await client.get(`/workout-sessions/last-weights/${encodeURIComponent(exerciseName)}`);
      return response.data;
    } catch { return { weights: [], suggested_increase: 0, suggested_decrease: 0 }; }
  };

  const updateSetData = (exerciseIndex, setIndex, field, value) => {
    setWorkoutData(prev => prev.map((ex, i) =>
      i === exerciseIndex ? {
        ...ex, sets: ex.sets.map((s, j) => j === setIndex ? { ...s, [field]: parseFloat(value) || 0 } : s)
      } : ex
    ));
  };

  // Time-based exercises (cardio + planks) store duration in seconds.
  // The UI splits it into MM + SS inputs; this setter rebuilds the total.
  const updateSetDuration = (exerciseIndex, setIndex, part, value) => {
    const raw = Math.max(0, parseInt(value, 10) || 0);
    setWorkoutData(prev => prev.map((ex, i) => {
      if (i !== exerciseIndex) return ex;
      return {
        ...ex,
        sets: ex.sets.map((s, j) => {
          if (j !== setIndex) return s;
          const current = parseInt(s.duration_seconds, 10) || 0;
          const curMin = Math.floor(current / 60);
          const curSec = current % 60;
          const minutes = part === 'minutes' ? raw : curMin;
          const seconds = part === 'seconds' ? Math.min(59, raw) : curSec;
          return { ...s, duration_seconds: minutes * 60 + seconds };
        }),
      };
    }));
  };

  // Generic cardio-metric setter. SetRow's getCardioFields column descriptors
  // already convert the raw <Input> string to the number to store; the
  // Number.isFinite guard is belt-and-suspenders for any other caller that
  // might pass a string/NaN (0 = "not entered" → renders as placeholder).
  const updateSetCardioField = (exerciseIndex, setIndex, key, value) => {
    const num = Number.isFinite(value) ? value : 0;
    setWorkoutData(prev => prev.map((ex, i) =>
      i === exerciseIndex ? {
        ...ex, sets: ex.sets.map((s, j) => j === setIndex ? { ...s, [key]: num } : s)
      } : ex
    ));
  };

  const updateSetRPE = (exerciseIndex, setIndex, value) => {
    setWorkoutData(prev => prev.map((ex, i) =>
      i === exerciseIndex ? {
        ...ex, sets: ex.sets.map((s, j) => j === setIndex ? { ...s, rpe: value ? parseFloat(value) : null } : s)
      } : ex
    ));
  };

  const changeSetType = (exerciseIndex, setIndex, newType) => {
    setWorkoutData(prev => prev.map((ex, i) => {
      if (i !== exerciseIndex) return ex;
      let backoffWeight = null;
      if (newType === 'backoff') {
        const lastWorkSet = [...ex.sets].reverse().find(s => (s.set_type || 'work') === 'work');
        if (lastWorkSet?.weight) backoffWeight = Math.round(lastWorkSet.weight * 0.65 * 2) / 2; // round to nearest 0.5kg
      }
      return {
        ...ex,
        sets: ex.sets.map((s, j) => {
          if (j !== setIndex) return s;
          const updated = { ...s, set_type: newType };
          if (backoffWeight !== null) updated.weight = backoffWeight;
          return updated;
        })
      };
    }));
  };

  const proceedCompleteSet = (exerciseIndex, setIndex) => {
    const key = `${exerciseIndex}-${setIndex}`;
    const exerciseForSet = workoutData[exerciseIndex];
    // Auto-fill completed_reps with target_reps_min if user didn't enter reps
    // (only for weight-based exercises — cardio/planks have no reps).
    const currentSet = exerciseForSet?.sets[setIndex];
    if (!isTimeBasedExercise(exerciseForSet) && currentSet && !currentSet.completed_reps && currentSet.target_reps_min) {
      setWorkoutData(prev => prev.map((ex, i) =>
        i === exerciseIndex ? {
          ...ex, sets: ex.sets.map((s, j) => j === setIndex ? { ...s, completed_reps: s.target_reps_min } : s)
        } : ex
      ));
    }
    setCompletedSets(prev => ({ ...prev, [key]: true }));
    setIncompleteSets(prev => { const n = new Set(prev); n.delete(key); return n; });
    haptics.medium();
    toast.success(t('workout.setCompletedToast', { num: setIndex + 1 }));
    // Auto-start rest timer (if enabled in preferences)
    // For supersets: only start rest after the LAST exercise in the group
    const inSuperset = supersetGroups[exerciseIndex] !== undefined;
    const isLastInSuperset = inSuperset && (() => {
      const groupId = supersetGroups[exerciseIndex];
      const groupIndices = Object.entries(supersetGroups).filter(([, g]) => g === groupId).map(([i]) => parseInt(i)).sort((a, b) => a - b);
      return exerciseIndex === groupIndices[groupIndices.length - 1];
    })();

    if (workoutPrefs.autoStartRestTimer && (!inSuperset || isLastInSuperset)) {
      const exRestTime = workoutData[exerciseIndex]?.rest_seconds || defaultRestSeconds[exerciseIndex] || workoutPrefs.defaultRestSeconds;
      startRestTimer(exRestTime, workoutData[exerciseIndex]?.name, { beepEnabled: workoutPrefs.restBeepEnabled });
    }

    // PR celebration — fired on work/failure sets only, compared against recent history.
    // The backend does the authoritative all-time check on save; this is the optimistic
    // in-workout popup. Weighted sets → weight PR; bodyweight (0 kg) sets → reps PR.
    const exName = workoutData[exerciseIndex]?.name;
    const setData = workoutData[exerciseIndex]?.sets[setIndex];
    const lw = lastWeights[exName];
    const setReps = setData ? (setData.completed_reps ?? setData.reps ?? 0) : 0;
    if (
      setData &&
      (setData.set_type === 'work' || setData.set_type === 'failure') &&
      lw && lw.weights && lw.weights.length > 0
    ) {
      if ((setData.weight || 0) > 0) {
        const prevMaxWeight = Math.max(0, ...lw.weights
          .filter(w => w.set_type === 'work' || w.set_type === 'failure')
          .map(w => w.weight || 0)
        );
        // Compare against both recent max AND highest weight already celebrated this session
        const sessionBest = sessionPrMaxRef.current[exName] || 0;
        const threshold = Math.max(prevMaxWeight, sessionBest);
        if (threshold > 0 && setData.weight > threshold) {
          sessionPrMaxRef.current[exName] = setData.weight;
          // Fire celebration via direct DOM — bypasses React render issues
          showPrCelebration({
            t,
            weight: setData.weight,
            exerciseName: exName,
            reps: setReps || 1,
            prType: 'weight',
            prevBest: threshold,
            soundEnabled: workoutPrefs.restBeepEnabled,
            onPostToFeed: (pr) => setPrCardPr(pr),
          });
        }
      } else if (setReps > 0) {
        // Bodyweight exercise — progression is reps. Compare against best reps from recent
        // sessions (lw.max_reps, present on batch-fetched routine exercises), the last
        // session's best reps, and the most reps already celebrated this session.
        const prevMaxRepsLastSession = Math.max(0, ...(lw.weights || [])
          .filter(w => w.set_type === 'work' || w.set_type === 'failure')
          .map(w => w.reps || 0)
        );
        const sessionBestReps = sessionPrRepsRef.current[exName] || 0;
        const threshold = Math.max(lw.max_reps || 0, prevMaxRepsLastSession, sessionBestReps);
        if (threshold > 0 && setReps > threshold) {
          sessionPrRepsRef.current[exName] = setReps;
          showPrCelebration({
            t,
            weight: 0,
            exerciseName: exName,
            reps: setReps,
            prType: 'reps',
            prevBest: threshold,
            soundEnabled: workoutPrefs.restBeepEnabled,
            onPostToFeed: (pr) => setPrCardPr(pr),
          });
        }
      }
    }
  };

  const proceedCompleteSetRef = useRef(proceedCompleteSet);
  useEffect(() => { proceedCompleteSetRef.current = proceedCompleteSet; });

  const completeSet = (exerciseIndex, setIndex, options = {}) => {
    const { bypassUnrealisticCheck = false } = options;
    const key = `${exerciseIndex}-${setIndex}`;
    if (completedSets[key]) {
      // Un-complete — gate does not apply to un-checking.
      setCompletedSets(prev => { const n = { ...prev }; delete n[key]; return n; });
      return;
    }
    const currentSet = workoutData[exerciseIndex]?.sets[setIndex];
    const exercise = workoutData[exerciseIndex];

    // Time-based exercises (cardio + planks) only need a duration. Skip every
    // weight/reps gate below — they don't apply.
    if (isTimeBasedExercise(exercise)) {
      if (!currentSet?.duration_seconds || currentSet.duration_seconds <= 0) {
        toast.warning(t('workout.zeroTimeWarning', { num: setIndex + 1 }), {
          duration: 6000,
          action: {
            label: t('workout.logAnyway'),
            onClick: () => proceedCompleteSetRef.current(exerciseIndex, setIndex),
          },
          cancel: { label: t('common.cancel') },
        });
        return;
      }
      proceedCompleteSet(exerciseIndex, setIndex);
      return;
    }

    // Unrealistic-value gate — runs on RAW entered values, before any auto-fill.
    // Auto-filled previous-session weights and target-reps shouldn't trigger
    // the dialog (those values were legitimate when they were saved).
    if (!bypassUnrealisticCheck && currentSet && exercise) {
      const historyStats = lastWeights[exercise.name] || {};
      const flags = computeUnrealisticFlags(
        currentSet.completed_reps,
        currentSet.weight,
        exercise.equipment,
        historyStats,
      );
      if (flags.reps || flags.weight) {
        setUnrealisticConfirmDialog({
          open: true,
          exerciseIndex,
          setIndex,
          exerciseName: exercise.name || '',
          reps: currentSet.completed_reps || 0,
          weight: currentSet.weight || 0,
          flags,
        });
        return;
      }
    }

    // Auto-fill weight from previous session if the user left it empty
    if ((!currentSet?.weight || currentSet.weight == 0) && !isBodyweightExercise(exercise?.equipment)) {
      const exName = exercise?.name;
      const lw = lastWeights[exName];
      const prevSet = getMatchingPreviousSet(lw?.weights, exercise?.sets, setIndex);
      if (prevSet?.weight > 0) {
        // Set weight and auto-fill reps in one state update, then mark complete
        setWorkoutData(prev => prev.map((ex, i) =>
          i === exerciseIndex ? {
            ...ex, sets: ex.sets.map((s, j) => {
              if (j !== setIndex) return s;
              return { ...s, weight: prevSet.weight, completed_reps: s.completed_reps || s.target_reps_min || 0 };
            })
          } : ex
        ));
        const key = `${exerciseIndex}-${setIndex}`;
        setCompletedSets(prev => ({ ...prev, [key]: true }));
        setIncompleteSets(prev => { const n = new Set(prev); n.delete(key); return n; });
        haptics.medium();
        toast.success(t('workout.setCompletedToast', { num: setIndex + 1 }));
        if (workoutPrefs.autoStartRestTimer) {
          const inSuperset = supersetGroups[exerciseIndex] !== undefined;
          const isLastInSuperset = inSuperset && (() => {
            const groupId = supersetGroups[exerciseIndex];
            const groupIndices = Object.entries(supersetGroups).filter(([, g]) => g === groupId).map(([i]) => parseInt(i)).sort((a, b) => a - b);
            return exerciseIndex === groupIndices[groupIndices.length - 1];
          })();
          if (!inSuperset || isLastInSuperset) {
            const exRestTime = workoutData[exerciseIndex]?.rest_seconds || defaultRestSeconds[exerciseIndex] || workoutPrefs.defaultRestSeconds;
            startRestTimer(exRestTime, workoutData[exerciseIndex]?.name, { beepEnabled: workoutPrefs.restBeepEnabled });
          }
        }
        return;
      }
      // No previous weight either — warn about 0 kg
      toast.warning(t('workout.zeroKgWarning', { num: setIndex + 1 }), {
        duration: 6000,
        action: {
          label: t('workout.logAnyway'),
          onClick: () => proceedCompleteSetRef.current(exerciseIndex, setIndex),
        },
        cancel: {
          label: t('common.cancel'),
        },
      });
      return;
    }
    proceedCompleteSet(exerciseIndex, setIndex);
  };

  const deleteSet = (exerciseIndex, setIndex) => {
    setWorkoutData(prev => prev.map((ex, i) => {
      if (i !== exerciseIndex) return ex;
      const newSets = ex.sets.filter((_, j) => j !== setIndex).map((s, j) => ({ ...s, set_number: j + 1 }));
      return { ...ex, sets: newSets };
    }));
    // Clean up completed sets for this exercise
    setCompletedSets(prev => {
      const n = {};
      Object.keys(prev).forEach(k => {
        const [ei, si] = k.split('-').map(Number);
        if (ei === exerciseIndex) {
          if (si < setIndex) n[k] = true;
          else if (si > setIndex) n[`${ei}-${si - 1}`] = true;
        } else n[k] = true;
      });
      return n;
    });
  };

  const addSetToExercise = (exerciseIndex) => {
    setWorkoutData(prev => prev.map((ex, i) =>
      i === exerciseIndex ? {
        ...ex, sets: [...ex.sets, {
          set_number: ex.sets.length + 1, set_type: 'work',
          target_reps_min: workoutPrefs.defaultRepsMin, target_reps_max: workoutPrefs.defaultRepsMax,
          from_routine: false,
          weight: ex.sets[ex.sets.length - 1]?.weight || 0,
          completed_reps: 0, is_pr: false, pr_types: [],
          _key: createSetKey()
        }]
      } : ex
    ));
  };

  const duplicateSet = (exerciseIndex, setIndex) => {
    setWorkoutData(prev => prev.map((ex, i) => {
      if (i !== exerciseIndex) return ex;
      const source = ex.sets[setIndex];
      if (!source) return ex;
      const clone = {
        ...source,
        _key: createSetKey(),
        completed_reps: 0,
        is_pr: false,
        pr_types: [],
        from_routine: false,
      };
      const newSets = [
        ...ex.sets.slice(0, setIndex + 1),
        clone,
        ...ex.sets.slice(setIndex + 1),
      ].map((s, j) => ({ ...s, set_number: j + 1 }));
      return { ...ex, sets: newSets };
    }));
    // Shift completed flags for sets after setIndex up by one
    setCompletedSets(prev => {
      const n = {};
      Object.keys(prev).forEach(k => {
        const [ei, si] = k.split('-').map(Number);
        if (ei === exerciseIndex && si > setIndex) n[`${ei}-${si + 1}`] = true;
        else n[k] = prev[k];
      });
      return n;
    });
  };

  const addSetBelow = (exerciseIndex, setIndex) => {
    setWorkoutData(prev => prev.map((ex, i) => {
      if (i !== exerciseIndex) return ex;
      const prevSet = ex.sets[setIndex];
      const blank = {
        set_number: 0, // will be renumbered below
        set_type: 'work',
        target_reps_min: workoutPrefs.defaultRepsMin,
        target_reps_max: workoutPrefs.defaultRepsMax,
        from_routine: false,
        weight: prevSet?.weight || 0,
        completed_reps: 0,
        is_pr: false,
        pr_types: [],
        _key: createSetKey(),
      };
      const newSets = [
        ...ex.sets.slice(0, setIndex + 1),
        blank,
        ...ex.sets.slice(setIndex + 1),
      ].map((s, j) => ({ ...s, set_number: j + 1 }));
      return { ...ex, sets: newSets };
    }));
    setCompletedSets(prev => {
      const n = {};
      Object.keys(prev).forEach(k => {
        const [ei, si] = k.split('-').map(Number);
        if (ei === exerciseIndex && si > setIndex) n[`${ei}-${si + 1}`] = true;
        else n[k] = prev[k];
      });
      return n;
    });
  };

  const fetchExerciseByName = async (name) => {
    // Check if already cached
    if (allExercisesData[name]) {
      return allExercisesData[name];
    }
    try {
      const res = await client.get(`/exercises?search=${encodeURIComponent(name)}`);
      // First try exact match
      let match = res.data.find(ex => ex.name.toLowerCase() === name.toLowerCase());

      // If no exact match, try partial match (exercise name contains search or search contains exercise name)
      if (!match && res.data.length > 0) {
        const searchLower = name.toLowerCase();
        match = res.data.find(ex => {
          const exNameLower = ex.name.toLowerCase();
          // Match if the search term is contained in exercise name or vice versa
          return exNameLower.includes(searchLower) || searchLower.includes(exNameLower);
        });
      }

      // If still no match, just take the first result from the search
      if (!match && res.data.length > 0) {
        match = res.data[0];
      }

      if (match) {
        setAllExercisesData(prev => ({ ...prev, [name]: match }));
        return match;
      }
    } catch (err) { toast.error(err?.response?.data?.detail || t('workout.failedLoadExercise')); }
    return null;
  };

  const openExerciseDetail = async (exName, exerciseIndex = null) => {
    // First check if we have enriched data in workoutData
    if (exerciseIndex !== null && workoutData[exerciseIndex]) {
      const exerciseData = workoutData[exerciseIndex];
      if (exerciseData.gifUrl || exerciseData.image_url || exerciseData.instructions) {
        const exId = exerciseData.id || exerciseData.exercise_id || exerciseData.name;
        setExerciseDetail({
          id: exId,
          name: exerciseData.name,
          muscle_group: exerciseData.muscle_group,
          image_url: exerciseData.image_url,
          image_url_2: exerciseData.image_url_2,
          gifUrl: exerciseData.gifUrl || exerciseData.image_url,
          instructions: exerciseData.instructions,
          instructions_list: exerciseData.instructions_list || [],
          primary_muscles: exerciseData.primary_muscles || [],
          secondary_muscles: exerciseData.secondary_muscles || [],
          targetMuscles: exerciseData.targetMuscles || exerciseData.primary_muscles || [],
          secondaryMuscles: exerciseData.secondaryMuscles || exerciseData.secondary_muscles || [],
          equipment: exerciseData.equipment
        });
        setShowExerciseDetail(true);
        exerciseDialog.open(exId);
        return;
      }
    }

    // Fall back to fetching from database
    const exerciseData = await fetchExerciseByName(exName);
    if (exerciseData) {
      setExerciseDetail(exerciseData);
      setShowExerciseDetail(true);
      exerciseDialog.open(exerciseData.id || exerciseData.name || exName);
    } else {
      // Show basic info if not found
      setExerciseDetail({ name: exName, instructions: 'No detailed instructions available for this exercise.' });
      setShowExerciseDetail(true);
      exerciseDialog.open(exName);
    }
  };

  const addAdhocExercise = async (exercise) => {
    const lastData = await fetchLastWeights(exercise.name);
    setLastWeights(prev => ({ ...prev, [exercise.name]: lastData }));

    setWorkoutData(prev => [...prev, {
      live_id: createWorkoutExerciseId(),
      exercise_id: exercise.id, name: exercise.name, muscle_group: exercise.muscle_group,
      notes: `Primary: ${(exercise.targetMuscles || exercise.primary_muscles || []).join(', ') || 'N/A'}`,
      is_adhoc: true,
      gifUrl: exercise.gifUrl || exercise.image_url,
      image_url: exercise.image_url,
      instructions: exercise.instructions,
      instructions_list: exercise.instructions_list || [],
      targetMuscles: exercise.targetMuscles || [],
      secondaryMuscles: exercise.secondaryMuscles || [],
      equipment: exercise.equipment,
      sets: Array.from({ length: defaultSetCount(exercise) }, (_, i) => ({
        set_number: i + 1, set_type: 'work',
        target_reps_min: workoutPrefs.defaultRepsMin, target_reps_max: workoutPrefs.defaultRepsMax,
        weight: lastData.weights[i]?.weight || 0, completed_reps: 0,
        is_pr: false, pr_types: [], _key: createSetKey(),
      })),
    }]);
    setShowAddExercise(false);
    setExerciseSearch('');
    setExerciseResults([]);
    toast.success(t('workout.exerciseAdded', { name: exercise.name }));
  };

  /* ── Swap Exercise helpers ─────────────────────────────────── */
  const openSwapModal = (exerciseIndex) => {
    const currentTarget = (workoutData[exerciseIndex]?.targetMuscles || [])[0] || '';
    const capitalizedTarget = currentTarget ? currentTarget.charAt(0).toUpperCase() + currentTarget.slice(1) : '';
    setSwapExerciseIndex(exerciseIndex);
    setSwapMuscleFilter(capitalizedTarget);
    setSwapSearch('');
    setShowSwapModal(true);
    loadSwapExercises('', capitalizedTarget);
  };

  const loadSwapExercises = async (search = '', muscle = '') => {
    try {
      let url = `/exercises?`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (muscle) url += `target_muscle=${encodeURIComponent(muscle.toLowerCase())}`;
      const res = await client.get(url);
      setSwapResults(res.data);
    } catch { setSwapResults([]); }
  };

  const confirmSwap = async (newExercise) => {
    if (swapExerciseIndex === null) return;
    const lastData = await fetchLastWeights(newExercise.name);
    setLastWeights(prev => ({ ...prev, [newExercise.name]: lastData }));

    setWorkoutData(prev => prev.map((ex, i) => {
      if (i !== swapExerciseIndex) return ex;
      return {
        ...ex,
        exercise_id: newExercise.id,
        name: newExercise.name,
        muscle_group: newExercise.muscle_group,
        notes: `Primary: ${(newExercise.targetMuscles || newExercise.primary_muscles || []).join(', ') || 'N/A'}`,
        is_adhoc: true,
        gifUrl: newExercise.gifUrl || newExercise.image_url,
        image_url: newExercise.image_url,
        instructions: newExercise.instructions,
        instructions_list: newExercise.instructions_list || [],
        targetMuscles: newExercise.targetMuscles || [],
        secondaryMuscles: newExercise.secondaryMuscles || [],
        equipment: newExercise.equipment,
        sets: ex.sets.map((s, idx) => ({
          ...s,
          weight: lastData.weights[idx]?.weight || 0,
          completed_reps: 0,
        }))
      };
    }));

    // Clear completed state for this exercise's sets
    setCompletedSets(prev => {
      const n = {};
      Object.keys(prev).forEach(k => {
        const [ei] = k.split('-').map(Number);
        if (ei !== swapExerciseIndex) n[k] = prev[k];
      });
      return n;
    });

    setShowSwapModal(false);
    setSwapExerciseIndex(null);
    toast.success(t('workout.exerciseSwapped', { name: newExercise.name }));
  };

  const getIncompleteSetsKeys = useCallback(() => {
    const keys = [];
    workoutData.forEach((ex, exIdx) => {
      ex.sets.forEach((set, setIdx) => {
        const key = `${exIdx}-${setIdx}`;
        if (!completedSets[key]) keys.push(key);
      });
    });
    return keys;
  }, [workoutData, completedSets]);

  const scrollToFirstIncompleteSet = useCallback((keys) => {
    if (!keys.length) return;
    const [exIdx, setIdx] = keys[0].split('-').map(Number);
    const el = document.querySelector(`[data-testid="set-${exIdx}-${setIdx}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  // Auto-dismiss rest timer once every set is checked — there's nothing left
  // to rest for, so the global pill should not linger on other pages.
  useEffect(() => {
    if (!restTimerRunning) return;
    if (workoutData.length === 0) return;
    const totalSets = workoutData.reduce((acc, ex) => acc + ex.sets.length, 0);
    if (totalSets === 0) return;
    const completedCount = Object.keys(completedSets).length;
    if (completedCount >= totalSets) skipRestTimer();
  }, [workoutData, completedSets, restTimerRunning, skipRestTimer]);

  const finishWorkout = async () => {
    setIsTimerRunning(false);
    if (workoutData.length === 0) {
      toast.error(t('workout.addExerciseFirst'));
      setIsTimerRunning(true);
      return;
    }

    // Block if any sets are unchecked (incomplete)
    const incompleteKeys = getIncompleteSetsKeys();
    if (incompleteKeys.length > 0) {
      setIncompleteSets(new Set(incompleteKeys));
      setShowIncompleteAlert(true);
      setIsTimerRunning(true);
      return;
    }

    proceedFinishWorkout();
  };

  const proceedFinishWorkout = () => {
    setShowIncompleteAlert(false);
    setIncompleteSets(new Set());
    setIsTimerRunning(false);

    // Calculate enhanced stats
    const totalSets = workoutData.reduce((acc, ex) => acc + ex.sets.length, 0);
    const completedSetsCount = Object.keys(completedSets).length;
    // Time-based sets (cardio + planks) carry duration_seconds, not weight×reps —
    // skip them in the kg-volume tally so a leg day's volume isn't skewed by a
    // 30-min treadmill warm-up, and a pure cardio session reads 0kg / Xmin.
    const totalVolume = workoutData.reduce((acc, ex) => {
      if (isTimeBasedExercise(ex)) return acc;
      return acc + ex.sets.reduce((setAcc, s) => setAcc + ((s.weight || 0) * (s.completed_reps || 0)), 0);
    }, 0);
    const muscleGroups = [...new Set(workoutData.flatMap(ex => ex.targetMuscles || []).filter(Boolean))];
    const durationMinutes = startedAt ? Math.floor((Date.now() - startedAt) / 60000) : 0;

    // Calculate volume per target muscle for the chart
    const volumeByMuscle = {};
    workoutData.forEach(ex => {
      if (isTimeBasedExercise(ex)) return;
      const targets = ex.targetMuscles || [];
      const vol = ex.sets.reduce((acc, s) => acc + ((s.weight || 0) * (s.completed_reps || 0)), 0);
      if (targets.length > 0) {
        targets.forEach(m => { volumeByMuscle[m] = (volumeByMuscle[m] || 0) + vol; });
      } else {
        const mg = ex.muscle_group || 'Other';
        volumeByMuscle[mg] = (volumeByMuscle[mg] || 0) + vol;
      }
    });

    const enhancedWorkout = {
      routine_id: assignedRoutine?.id || 'custom-' + Date.now(),
      day_id: activeWorkout?.id || 'custom-day',
      day_name: activeWorkout?.day_name || 'Custom Workout',
      duration_minutes: durationMinutes,
      total_sets: totalSets,
      completed_sets: completedSetsCount,
      total_volume: Math.round(totalVolume),
      muscle_groups: muscleGroups,
      volume_by_muscle: volumeByMuscle,
      exercises_raw: workoutData,
      exercises_detail: workoutData.map((ex, exIdx) => ({
        name: ex.name,
        muscle_group: ex.muscle_group,
        targetMuscles: ex.targetMuscles || [],
        session_notes: sessionNotes[exIdx] || '',
        sets: ex.sets.map(s => ({
          weight: s.weight || 0,
          reps: s.completed_reps || 0,
          duration_seconds: s.duration_seconds || 0,
          type: s.set_type || 'work',
          is_pr: s.is_pr || false, pr_types: s.pr_types || [],
          rpe: s.rpe || null
        }))
      }))
    };

    setCompletedWorkout(enhancedWorkout);
    setCompletionTab('summary');
    setCompletionPhoto(null);
    setCompletionDescription('');
    setCompletionVisibility('public');
    setEditDuration(durationMinutes);
    // Default the Start Time input to when the workout actually started
    // (falls back to now − duration if startedAt is missing). On save we
    // compute completed_at = start + duration, so the post timestamp reflects
    // the END of the workout. Build from LOCAL components because
    // datetime-local inputs interpret their value as local time — using
    // toISOString() here would shift by the tz offset.
    const pad = (n) => String(n).padStart(2, '0');
    const toLocalInput = (d) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    const startDate = startedAt
      ? new Date(startedAt)
      : new Date(Date.now() - durationMinutes * 60 * 1000);
    setWorkoutDate(toLocalInput(startDate));
    setShowCompletionDialog(true);
  };

  const saveAndShareWorkoutMutation = useMutation({
    // Capture the user's currently-earned badges BEFORE the save runs. onMutate
    // fires before mutationFn, so this reflects the true pre-save state (the
    // backend awards new badges in a post-save background task). Reading the
    // React Query cache here is unreliable — ['achievements','self'] is only ever
    // populated by visiting the Trophy Case page, so for most clients it was empty
    // and the post-save diff treated EVERY earned badge as new, re-celebrating the
    // latest one (e.g. "50 workouts") on every single workout. Fetch fresh instead.
    onMutate: async () => {
      if (onBehalfOf) return { priorBadgeIds: null };
      try {
        const data = (await client.get('/achievements')).data;
        return { priorBadgeIds: new Set((data?.achievements || []).map(a => a.badge_id)) };
      } catch {
        // Couldn't establish a baseline — fail safe by skipping the celebration
        // rather than risking the every-workout spam bug.
        return { priorBadgeIds: null };
      }
    },
    mutationFn: async () => {
      const finalDuration = editDuration != null ? editDuration : completedWorkout.duration_minutes;
      const postUrl = onBehalfOf ? `/workout-sessions?on_behalf_of=${onBehalfOf}` : `/workout-sessions`;
      const response = await client.post(postUrl, {
        routine_id: completedWorkout.routine_id,
        day_id: completedWorkout.day_id,
        exercises: completedWorkout.exercises_raw.map((ex, exIdx) => ({
          exercise_id: ex.exercise_id || ex.name, name: ex.name,
          muscle_group: ex.muscle_group, targetMuscles: ex.targetMuscles || [],
          notes: sessionNotes[exIdx] || ex.notes || '',
          sets: ex.sets.map(s => ({
            set_number: s.set_number, set_type: s.set_type || 'work',
            target_reps_min: s.target_reps_min || 0, target_reps_max: s.target_reps_max || 0,
            weight: s.weight || 0, completed_reps: s.completed_reps || 0,
            duration_seconds: s.duration_seconds || 0,
            speed_kmh: s.speed_kmh || 0, incline_pct: s.incline_pct || 0,
            distance_m: s.distance_m || 0, resistance_level: s.resistance_level || 0,
            is_pr: s.is_pr || false, pr_types: s.pr_types || [],
            rpe: s.rpe || null
          }))
        })),
        duration_minutes: finalDuration,
        description: completionDescription,
        visibility: completionVisibility,
        photo: completionPhoto || null,
        // workoutDate is the START time (local) from the datetime-local input.
        // The saved completed_at should be the END of the workout, so add
        // the final duration before sending to the backend.
        workout_date: workoutDate
          ? new Date(new Date(workoutDate).getTime() + (finalDuration || 0) * 60 * 1000).toISOString()
          : null,
      });
      return response;
    },
    onSuccess: async (_data, _variables, context) => {
      clearWorkoutStorage();
      skipRestTimer();
      setShowCompletionDialog(false);
      setActiveWorkout(null);
      setWorkoutData([]);
      setCompletedSets({});
      setStartedAt(null);
      setOnBehalfOf(null);
      setClientName(null);
      navigate('/workout', { replace: true });
      toast.success(onBehalfOf ? t('workout.workoutSavedFor', { name: clientName || 'client' }) : t('workout.workoutSaved'));
      queryClient.invalidateQueries({ queryKey: ['workoutStreak'] });
      queryClient.invalidateQueries({ queryKey: ['dailyStreak'] });

      // Detect newly-earned badges and celebrate the most-recent one.
      // priorBadgeIds is the authoritative pre-save baseline captured in onMutate.
      // When it's null (coach logging on behalf, or the baseline fetch failed) we
      // skip celebration entirely rather than risk re-celebrating old badges.
      if (!onBehalfOf && context?.priorBadgeIds) {
        try {
          const priorIds = context.priorBadgeIds;
          await queryClient.invalidateQueries({ queryKey: ['achievements'] });
          const fresh = await queryClient.fetchQuery({
            queryKey: ['achievements', 'self'],
            queryFn: async () => (await client.get('/achievements')).data,
          });
          const newly = (fresh?.achievements || []).filter(a => !priorIds.has(a.badge_id));
          if (newly.length > 0) {
            const latest = newly[0];
            showBadgeCelebration(latest, {
              t,
              onShare: () => setShareBadgeDialog({ open: true, badge: latest }),
            });
          }
        } catch (e) {
          // Badge detection is non-critical
          console.error('Badge celebration check failed', e);
        }
      }

      if (sourcePersonalRoutineId && sourcePersonalDayId) {
        setShowUpdateRoutinePrompt(true);
      }
    },
    onError: (err) => {
      console.error('Workout save error:', err);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const reason = detail || (status ? `HTTP ${status}` : err?.message || 'network error');
      toast.error(`${t('workout.failedSaveWorkout')}: ${reason}`);
    },
  });

  const sharingToSocial = saveAndShareWorkoutMutation.isPending;

  const saveAndShareWorkout = () => {
    if (!completedWorkout) return;
    saveAndShareWorkoutMutation.mutate();
  };

  const discardWorkout = () => {
    skipRestTimer();
    setIsTimerRunning(false);
    clearWorkoutStorage();
    setShowCompletionDialog(false);
    setShowDiscardConfirm(false);
    setActiveWorkout(null);
    setWorkoutData([]);
    setCompletedSets({});
    setStartedAt(null);
    setOnBehalfOf(null);
    setClientName(null);
    sessionPrMaxRef.current = {};
    sessionPrRepsRef.current = {};
    toast.success(t('workout.workoutDiscarded'));
    navigate('/workout', { replace: true });
  };

  // Handle photo upload for completion - open cropper with 9:16 aspect ratio
  const handleCompletionPhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('workout.photoTooLarge'));
      return;
    }
    cropperActiveRef.current = true;
    const reader = new FileReader();
    reader.onload = () => {
      setCropperSrc(reader.result);
      setCropperOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const reorderExercises = useCallback((activeId, overId) => {
    if (!overId || activeId === overId) return;

    const oldIndex = workoutData.findIndex((exercise) => exercise.live_id === activeId);
    const newIndex = workoutData.findIndex((exercise) => exercise.live_id === overId);

    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

    const reorderedWorkout = arrayMove(workoutData, oldIndex, newIndex);
    const indexMap = buildReorderIndexMap(workoutData, reorderedWorkout);

    setWorkoutData(reorderedWorkout);
    setCompletedSets((prev) => remapCompletedSets(prev, indexMap));
    setSessionNotes((prev) => remapIndexedRecord(prev, indexMap));
    setExpandedNotes((prev) => remapIndexedRecord(prev, indexMap));
    setSupersetGroups((prev) => remapIndexedRecord(prev, indexMap));
    setDefaultRestSeconds((prev) => remapIndexedRecord(prev, indexMap));
    setAppliedProgression((prev) => remapAppliedProgression(prev, indexMap, workoutSessionKey));
  }, [workoutData, workoutSessionKey, setWorkoutData, setAppliedProgression, setCompletedSets]);

  /* ════════════════════════════════════════════════════════════
     Completion Dialog JSX (rendered as variable, not component,
     so it doesn't unmount/remount during the activeWorkout =>
     null transition mid-save).
     ════════════════════════════════════════════════════════════ */
  const completionDialogJSX = (
    <>
    {/* ── Incomplete Sets Alert ─────────────────────────────────── */}
    <Dialog open={showIncompleteAlert} onOpenChange={setShowIncompleteAlert}>
      <DialogContent className="max-w-sm" data-testid="incomplete-sets-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500/15 text-red-500">
              <X className="h-4 w-4" />
            </span>
            {t('workout.incompleteSets.title')}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{t('workout.incompleteSets.body', { count: incompleteSets.size })}</span>
          {' '}{t('workout.incompleteSets.instruction')}
        </p>
        <DialogFooter className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => {
              setShowIncompleteAlert(false);
              const keys = [...incompleteSets].sort();
              scrollToFirstIncompleteSet(keys);
            }}
            data-testid="fix-incomplete-btn"
          >
            {t('workout.incompleteSets.fixBtn')}
          </Button>
          <Button
            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            onClick={proceedFinishWorkout}
            data-testid="finish-anyway-btn"
          >
            {t('workout.incompleteSets.finishAnyway')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={showCompletionDialog && !cropperOpen} onOpenChange={(open) => { if (!open && !cropperActiveRef.current) setShowCompletionDialog(false); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden max-h-[90dvh] [display:flex!important] flex-col gap-0" data-testid="workout-completion-dialog">
        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 px-4 pt-3 pb-3 text-center text-white shrink-0">
          {onBehalfOf && clientName && (
            <div className="mb-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur inline-flex items-center gap-1.5 text-xs font-medium">
              <Users className="h-3.5 w-3.5" />
              {t('workout.loggingFor', { name: clientName })}
            </div>
          )}
          <div className="mx-auto w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center mb-1.5">
            <Trophy className="h-5 w-5 text-white" />
          </div>
          <DialogTitle className="text-xl font-bold mb-0.5" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {t('workout.workoutComplete')}
          </DialogTitle>
          <p className="text-white/80 text-xs">{completedWorkout?.day_name || 'Custom Workout'}</p>
        </div>

        {/* Stats Resume Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border-b bg-muted/30 shrink-0">
          <div className="p-2 text-center border-r">
            <div className="flex items-center justify-center gap-1">
              <button
                onClick={() => setEditDuration(d => Math.max(1, (d || completedWorkout?.duration_minutes || 0) - 5))}
                className="w-5 h-5 rounded-full bg-muted hover:bg-muted-foreground/20 flex items-center justify-center text-xs font-bold"
                data-testid="duration-minus-btn"
              >-</button>
              <input
                type="number"
                value={editDuration != null ? editDuration : (completedWorkout?.duration_minutes || 0)}
                onChange={(e) => setEditDuration(Math.max(0, parseInt(e.target.value) || 0))}
                className="w-10 text-center text-base font-bold font-mono text-foreground bg-transparent border-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                data-testid="duration-input"
              />
              <button
                onClick={() => setEditDuration(d => (d || completedWorkout?.duration_minutes || 0) + 5)}
                className="w-5 h-5 rounded-full bg-muted hover:bg-muted-foreground/20 flex items-center justify-center text-xs font-bold"
                data-testid="duration-plus-btn"
              >+</button>
            </div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('workout.statMin')}</p>
          </div>
          <div className="p-2 text-center border-r">
            <p className="text-base font-bold font-mono text-foreground">{completedWorkout?.total_volume ? (completedWorkout.total_volume >= 1000 ? `${(completedWorkout.total_volume/1000).toFixed(1)}k` : completedWorkout.total_volume) : 0}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('workout.statVolume')}</p>
          </div>
          <div className="p-2 text-center border-r">
            <p className="text-base font-bold font-mono text-foreground">{completedWorkout?.total_sets || 0}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('workout.statSets')}</p>
          </div>
          <div className="p-2 text-center">
            <p className="text-base font-bold font-mono text-foreground">{completedWorkout?.exercises_detail?.length || 0}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('workout.statExercises')}</p>
          </div>
        </div>

        <ScrollArea className="flex-1 min-h-0">
          <div className="p-4 space-y-3">
            {/* Workout Date/Time */}
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('workout.workoutStartTime')}</p>
              <input
                type="datetime-local"
                value={workoutDate || ''}
                onChange={(e) => setWorkoutDate(e.target.value)}
                max={(() => {
                  const d = new Date();
                  const p = (n) => String(n).padStart(2, '0');
                  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
                })()}
                className="w-full px-3 py-2 text-sm rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                data-testid="workout-date-input"
              />
            </div>

            {/* PRs Achievement — Multi-type colored trophies */}
            {completedWorkout?.prs_achieved > 0 && (() => {
              const allPrTypes = [...new Set(
                completedWorkout.exercises?.flatMap(ex =>
                  ex.sets?.flatMap(s => s.pr_types || (s.is_pr ? ['weight'] : [])) || []
                ) || []
              )];
              const prLabels = { weight: t('workout.prWeight'), e1rm: t('workout.pr1rm'), reps: t('workout.prReps'), progress: t('workout.prProgress'), alltime: t('workout.prAlltime') };
              const prColors = { weight: 'text-yellow-500', e1rm: 'text-purple-500', reps: 'text-green-500', progress: 'text-yellow-500', alltime: 'text-purple-500' };

              // Flatten PRs (one entry per PR type per set)
              const flatPrs = [];
              (completedWorkout.exercises || []).forEach(ex => {
                (ex.sets || []).forEach(s => {
                  const types = s.pr_types || (s.is_pr ? ['weight'] : []);
                  const setReps = s.completed_reps ?? s.reps ?? 1;
                  types.forEach(tType => {
                    flatPrs.push({
                      exerciseName: ex.name,
                      weight: s.weight || 0,
                      reps: setReps || 1,
                      prType: tType,
                    });
                  });
                });
              });

              const handleSharePrs = () => {
                if (flatPrs.length === 0) return;
                if (flatPrs.length === 1) setPrCardPr(flatPrs[0]);
                else { setPrPickerList(flatPrs); setPrPickerOpen(true); }
              };

              return (
                <div className="p-2.5 rounded-xl bg-gradient-to-r from-yellow-500/20 via-green-500/10 to-purple-500/20 border border-yellow-500/30 text-center space-y-2">
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    {allPrTypes.map(type => (
                      <span key={type} className="flex items-center gap-1">
                        <Trophy className={`h-4 w-4 ${prColors[type] || 'text-yellow-500'}`} />
                        <span className={`font-bold text-sm ${prColors[type] || 'text-yellow-500'}`}>{prLabels[type] || 'PR'}</span>
                      </span>
                    ))}
                    <span className="font-bold text-yellow-600 dark:text-yellow-400 text-sm">
                      — {completedWorkout.prs_achieved} exercise{completedWorkout.prs_achieved > 1 ? 's' : ''} with new PRs!
                    </span>
                  </div>
                  {flatPrs.length > 0 && (
                    <button
                      onClick={handleSharePrs}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary hover:bg-primary/90 text-white"
                      data-testid="workout-complete-share-prs"
                    >
                      <Send className="h-3.5 w-3.5" />
                      {t('pr.sharePrs', 'Share PRs')}
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Photo Upload */}
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('workout.addAPhoto')}</p>
              {completionPhoto ? (
                <div
                  className="relative rounded-xl overflow-hidden mx-auto border border-green-500"
                  style={{ aspectRatio: '4/5', maxHeight: '280px' }}
                >
                  <LazyImage src={completionPhoto} alt="Workout" wrapperClassName="w-full h-full" className="w-full h-full object-cover" />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setCompletionPhoto(null)}
                        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80"
                      >
                        <X className="h-3.5 w-3.5 text-white" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{t('tooltips.removePhoto')}</TooltipContent>
                  </Tooltip>
                </div>
              ) : (
                <div className="flex gap-2">
                  <label htmlFor="completion-photo-camera" className="flex-1 cursor-pointer">
                    <div className="flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-border hover:border-primary/50 hover:text-foreground transition-colors text-muted-foreground">
                      <Camera className="h-4 w-4" />
                      <span className="text-xs font-medium">{t('workout.camera')}</span>
                    </div>
                    <input
                      id="completion-photo-camera"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={handleCompletionPhotoUpload}
                    />
                  </label>
                  <label htmlFor="completion-photo-gallery" className="flex-1 cursor-pointer">
                    <div className="flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-border hover:border-primary/50 hover:text-foreground transition-colors text-muted-foreground">
                      <Images className="h-4 w-4" />
                      <span className="text-xs font-medium">{t('workout.gallery')}</span>
                    </div>
                    <input
                      id="completion-photo-gallery"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleCompletionPhotoUpload}
                      data-testid="completion-photo-input"
                    />
                  </label>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('workout.descriptionLabel')}</p>
              <textarea
                value={completionDescription}
                onChange={(e) => setCompletionDescription(e.target.value)}
                placeholder={t('workout.howWasWorkout')}
                className="w-full h-16 px-3 py-2 text-sm rounded-xl border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                data-testid="completion-description"
              />
            </div>

            {/* Visibility Toggle */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50">
              <div className="flex items-center gap-2">
                {completionVisibility === 'public' ? <Globe className="h-4 w-4 text-primary" /> : <Lock className="h-4 w-4 text-muted-foreground" />}
                <div>
                  <p className="text-sm font-medium">{completionVisibility === 'public' ? t('workout.visibilityPublic') : t('workout.visibilityPrivate')}</p>
                  <p className="text-xs text-muted-foreground">{completionVisibility === 'public' ? t('workout.visibleOnFeed') : t('workout.onlyYouSee')}</p>
                </div>
              </div>
              <button
                onClick={() => setCompletionVisibility(v => v === 'public' ? 'private' : 'public')}
                className={`w-12 h-7 rounded-full transition-colors relative ${completionVisibility === 'public' ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                data-testid="visibility-toggle"
              >
                <div className={`w-5 h-5 rounded-full bg-white absolute top-1 transition-transform ${completionVisibility === 'public' ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* Muscles Worked */}
            {completedWorkout?.muscle_groups?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('workout.musclesWorkedLabel')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {completedWorkout.muscle_groups.map(mg => (
                    <Badge key={mg} variant="secondary" className={`text-xs capitalize ${TARGET_MUSCLE_COLORS[mg] || ''}`}>{translateMuscle(mg, language)}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Volume Distribution Chart */}
            {completedWorkout?.volume_by_muscle && Object.keys(completedWorkout.volume_by_muscle).length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('workout.volumeDistribution')}</p>
                <div className="space-y-1.5">
                  {(() => {
                    const entries = Object.entries(completedWorkout.volume_by_muscle).sort((a, b) => b[1] - a[1]);
                    const maxVol = Math.max(...entries.map(([, v]) => v));
                    const barColors = ['bg-primary', 'bg-blue-500', 'bg-purple-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500', 'bg-cyan-500'];
                    return entries.map(([muscle, volume], i) => (
                      <div key={muscle} className="flex items-center gap-2" data-testid={`volume-bar-${muscle}`}>
                        <span className="text-xs font-medium w-24 truncate text-right">{muscle}</span>
                        <div className="flex-1 h-5 bg-muted/50 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${barColors[i % barColors.length]} transition-all duration-500`}
                            style={{ width: `${maxVol > 0 ? (volume / maxVol) * 100 : 0}%` }}
                          />
                        </div>
                        <span className="text-xs font-mono text-muted-foreground w-14 text-right">
                          {volume >= 1000 ? `${(volume / 1000).toFixed(1)}k` : Math.round(volume)}kg
                        </span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer Actions */}
        <div className="p-3 border-t space-y-1.5 shrink-0">
          <Button
            onClick={saveAndShareWorkout}
            disabled={sharingToSocial}
            className="w-full bg-primary hover:bg-primary/90 h-10"
            data-testid="save-workout-btn"
          >
            {sharingToSocial ? (
              <><Loader2 className="animate-spin mr-2 h-4 w-4" /> {t('workout.savingLabel')}</>
            ) : (
              <><Check className="mr-2 h-4 w-4" /> {t('workout.saveWorkoutBtn')}</>
            )}
          </Button>
          <div className="grid grid-cols-2 gap-1.5">
            <Button
              onClick={() => {
                setSaveRoutineDayName(completedWorkout?.day_name || 'Workout');
                setSaveRoutineName('My Routine');
                setSaveRoutineChoice('new');
                setSaveRoutineTarget('');
                setShowSaveAsRoutine(true);
              }}
              variant="outline"
              className="border-primary/30 text-primary hover:bg-primary/90 h-9 text-xs"
              data-testid="save-as-routine-btn"
            >
              <Dumbbell className="mr-1.5 h-3.5 w-3.5" /> {t('workout.saveAsRoutineBtn')}
            </Button>
            <Button
              onClick={() => setShowCompletionStory(true)}
              variant="outline"
              className="border-primary/30 text-primary hover:bg-primary/90 h-9 text-xs"
              data-testid="create-story-btn"
            >
              <Share2 className="mr-1.5 h-3.5 w-3.5" /> {t('workout.createStoryBtn')}
            </Button>
          </div>
          <Button
            onClick={() => setShowDiscardConfirm(true)}
            variant="ghost"
            className="w-full text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 h-8 text-xs"
            data-testid="discard-workout-btn"
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> {t('workout.discardWorkoutBtn')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Discard Confirmation */}
    <Dialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
      <DialogContent className="max-w-sm" data-testid="discard-confirm-dialog">
        <DialogTitle className="text-lg font-bold">{t('workout.discardConfirmTitle')}</DialogTitle>
        <p className="text-sm text-muted-foreground">{t('workout.discardConfirmBody')}</p>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={() => setShowDiscardConfirm(false)} className="flex-1">{t('common.cancel')}</Button>
          <Button variant="destructive" onClick={discardWorkout} className="flex-1" data-testid="confirm-discard-btn">{t('workout.discardWorkoutBtn')}</Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Save as Routine Dialog */}
    <Dialog open={showSaveAsRoutine} onOpenChange={setShowSaveAsRoutine}>
      <DialogContent className="max-w-sm" data-testid="save-as-routine-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Dumbbell className="h-5 w-5 text-primary" /> {t('workout.saveAsRoutineTitle')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('workout.dayNameLabel')}</label>
            <Input
              value={saveRoutineDayName}
              onChange={e => setSaveRoutineDayName(e.target.value)}
              placeholder={t('workout.dayNamePlaceholder')}
              className="mt-1"
              data-testid="save-routine-day-name"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('workout.saveToLabel')}</label>
            <div className="flex gap-2">
              <button
                onClick={() => setSaveRoutineChoice('new')}
                className={`flex-1 p-3 rounded-xl border-2 text-left transition-colors ${saveRoutineChoice === 'new' ? 'border-primary bg-primary/10' : 'border-border hover:border-border/80'}`}
                data-testid="save-routine-new"
              >
                <Plus className="h-4 w-4 text-primary mb-1" />
                <p className="text-sm font-medium">{t('workout.newRoutineOption')}</p>
              </button>
              <button
                onClick={() => setSaveRoutineChoice('existing')}
                disabled={personalRoutines.length === 0}
                className={`flex-1 p-3 rounded-xl border-2 text-left transition-colors ${saveRoutineChoice === 'existing' ? 'border-primary bg-primary/10' : 'border-border hover:border-border/80'} ${personalRoutines.length === 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
                data-testid="save-routine-existing"
              >
                <Dumbbell className="h-4 w-4 text-primary mb-1" />
                <p className="text-sm font-medium">{t('workout.existingRoutineOption')}</p>
                <p className="text-xs text-muted-foreground">{personalRoutines.length} routine{personalRoutines.length !== 1 ? 's' : ''}</p>
              </button>
            </div>
          </div>
          {saveRoutineChoice === 'new' && (
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('workout.routineNameLabel')}</label>
              <Input
                value={saveRoutineName}
                onChange={e => setSaveRoutineName(e.target.value)}
                placeholder={t('workout.routineNamePlaceholder')}
                className="mt-1"
                data-testid="save-routine-name-input"
              />
            </div>
          )}
          {saveRoutineChoice === 'existing' && personalRoutines.length > 0 && (
            <div className="space-y-2">
              {personalRoutines.map(r => (
                <button
                  key={r.id}
                  onClick={() => setSaveRoutineTarget(r.id)}
                  className={`w-full p-3 rounded-xl border-2 text-left transition-colors ${saveRoutineTarget === r.id ? 'border-primary bg-primary/10' : 'border-border hover:border-border/80'}`}
                  data-testid={`save-routine-target-${r.id}`}
                >
                  <p className="text-sm font-semibold">{r.name}</p>
                  <p className="text-xs text-muted-foreground">{r.days?.length || 0} day{(r.days?.length || 0) !== 1 ? 's' : ''}</p>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowSaveAsRoutine(false)} className="flex-1">{t('common.cancel')}</Button>
          <Button
            onClick={saveWorkoutAsRoutine}
            disabled={savingRoutine || (saveRoutineChoice === 'existing' && !saveRoutineTarget)}
            className="flex-1 bg-primary hover:bg-primary/90"
            data-testid="confirm-save-routine-btn"
          >
            {savingRoutine ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : <Check className="mr-2 h-4 w-4" />}
            {t('common.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Image Cropper for workout completion photo */}
    <ImageCropper
      open={cropperOpen}
      onClose={() => { setCropperOpen(false); setCropperSrc(null); cropperActiveRef.current = false; setShowCompletionDialog(true); }}
      imageSrc={cropperSrc}
      onCropComplete={(dataUrl) => { setCompletionPhoto(dataUrl); setCropperOpen(false); setCropperSrc(null); cropperActiveRef.current = false; setShowCompletionDialog(true); }}
      aspectRatio={9 / 16}
      quality={0.92}
      title="Adjust Workout Photo"
      uploadContext="workout"
    />

    {/* Workout Story Generator */}
    <WorkoutStoryGenerator
      open={showCompletionStory}
      onClose={() => setShowCompletionStory(false)}
      workoutData={{
        dayName: completedWorkout?.day_name || 'Custom Workout',
        userName: user?.name,
        duration: editDuration != null ? editDuration : (completedWorkout?.duration_minutes || 0),
        exerciseCount: completedWorkout?.exercises_detail?.length || 0,
        setsCount: completedWorkout?.total_sets || 0,
        totalVolume: completedWorkout?.total_volume || 0,
        exercises: completedWorkout?.exercises_detail || [],
        photo: completionPhoto,
      }}
    />

    {/* PR card share + picker — opened from completion dialog or PR celebration */}
    <PrCardGenerator
      open={!!prCardPr}
      onClose={() => setPrCardPr(null)}
      pr={prCardPr}
      userName={user?.name}
    />
    <PrPickerSheet
      open={prPickerOpen}
      onClose={() => setPrPickerOpen(false)}
      prs={prPickerList}
      onPick={(pr) => { setPrPickerOpen(false); setPrCardPr(pr); }}
    />
    </>
  );

  // ── Render gating ───────────────────────────────────────────
  // Mount the completion dialog shell as long as either the live workout is
  // active OR the completion dialog is mid-show. The dialog must persist
  // across the activeWorkout=true→null transition that happens during a
  // successful save.
  if (!activeWorkout && !showCompletionDialog) {
    // Even if both are false, we still render the completion dialog shell
    // (no-op when closed) so that any deferred PR / story dialog state can
    // resolve cleanly. Returning null here would unmount those dialogs.
    return <>{completionDialogJSX}</>;
  }

  if (!activeWorkout) {
    // Workout is done but completion dialog is still open (mid-save flow).
    return <>{completionDialogJSX}</>;
  }

  /* ════════════════════════════════════════════════════════════
     ACTIVE WORKOUT VIEW — Infinite Scroll
     ════════════════════════════════════════════════════════════ */
  const totalSets = workoutData.reduce((acc, ex) => acc + ex.sets.length, 0);
  const completedCount = Object.keys(completedSets).length;
  const progress = totalSets > 0 ? (completedCount / totalSets) * 100 : 0;
  // Derive the routine that backs the active workout (for share menu).
  // Either the coach-assigned routine, or the personal routine the workout was started from.
  const activeRoutine = sourcePersonalRoutineId
    ? personalRoutines.find(r => r.id === sourcePersonalRoutineId)
    : assignedRoutine;
  // Sticky-header volume tally. With the timer extracted into <WorkoutTimer>, the
  // parent no longer re-renders every tick — so this reduce only runs on actual
  // set-state changes (completing / editing a set), not 1Hz.
  const totalCompletedVolumeKg = workoutData.reduce(
    (acc, ex, exIdx) => acc + ex.sets.reduce(
      (sAcc, s, sIdx) => sAcc + (completedSets[`${exIdx}-${sIdx}`] ? (s.weight || 0) * (s.completed_reps || 0) : 0),
      0,
    ),
    0,
  );

  /* ════════════════════════════════════════════════════════════
     liveActions — bag of LiveSession-bound handlers that ExerciseRow
     and SetRow need. Re-created each render (closures capture the
     latest local state); ExerciseRow's `useRef(liveActions)` mirror
     keeps SetRow callback refs stable across keystrokes.
     ════════════════════════════════════════════════════════════ */
  const handleSessionNoteChange = (exIdx, value) => {
    setSessionNotes(prev => ({ ...prev, [exIdx]: value }));
    // Mirror into workoutData[exIdx].session_note (NOT .notes — that field is
    // the coach-prescribed cue from the routine and must stay untouched during
    // the live session). session_note is a live-only field the
    // WorkoutSessionProvider's debounced localStorage persistence picks up —
    // without it the typed note lives only in LiveSession's local state and is
    // lost when the user navigates away from /workout mid-session.
    setWorkoutData(prev => prev.map((ex, i) => (i === exIdx ? { ...ex, session_note: value } : ex)));
  };

  const liveActions = {
    // data updates — proxied through to module-level helpers above
    updateSetData,
    updateSetDuration,
    updateSetCardioField,
    updateSetRPE,
    changeSetType,
    duplicateSet,
    addSetBelow,
    completeSet,
    deleteSet,
    addSetToExercise,
    uncompleteSet: (eIdx, sIdx) => {
      const key = `${eIdx}-${sIdx}`;
      setCompletedSets(prev => { const n = { ...prev }; delete n[key]; return n; });
    },
    toggleSetCompleted: (eIdx, sIdx, isDone) => {
      setCompletedSets(prev => ({ ...prev, [`${eIdx}-${sIdx}`]: !isDone }));
    },
    // long-press
    beginSetLongPress,
    moveSetLongPress,
    endSetLongPress,
    beginReorderLongPress,
    moveReorderLongPress,
    endReorderLongPress,
    consumeReorderLongPressTriggered: () => {
      if (reorderLongPressRef.current.triggered) {
        reorderLongPressRef.current.triggered = false;
        return true;
      }
      return false;
    },
    // context menus (set + exercise)
    openSetContextMenu: (eIdx, sIdx) => setSetContextMenu({ open: true, exIdx: eIdx, setIdx: sIdx }),
    closeSetContextMenu: () => setSetContextMenu({ open: false, exIdx: null, setIdx: null }),
    openExerciseContextMenu: (eIdx) => setExerciseContextMenu({ open: true, exIdx: eIdx }),
    closeExerciseContextMenu: () => setExerciseContextMenu({ open: false, exIdx: null }),
    setExerciseContextMenuOpen: (open, eIdx) => setExerciseContextMenu(open ? { open: true, exIdx: eIdx } : { open: false, exIdx: null }),
    // dialog openers / mode toggles
    openExerciseDetail,
    openSwapModal,
    enterReorderMode: () => setReorderMode(true),
    openPlateCalc: (weight, exName) => setPlateCalcDialog({ open: true, weight, exerciseName: exName }),
    openWarmupCalc: (config) => setWarmupDialog({ open: true, ...config }),
    openRemoveExerciseConfirm: (eIdx) => { setRemoveExerciseIndex(eIdx); setShowRemoveConfirm(true); },
    // exercise-level actions
    markAllSetsDone,
    focusExerciseNote,
    applyProgression,
    toggleSuperset,
    getSupersetColor,
    getSupersetBgColor,
  };

  return (
    <>
      <div className="pb-20 md:pb-0" data-testid="active-workout">
        {/* Sticky Workout Header */}
        <div className="sticky top-16 md:top-0 z-30 bg-background/95 backdrop-blur py-3 -mx-4 px-4 md:-mx-6 md:px-6 lg:-mx-8 lg:px-8 border-b border-border/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5 min-w-0">
              <MiniMuscleMap workoutData={workoutData} completedSets={completedSets} />
              <div className="min-w-0">
                <h1 className="text-xl font-bold truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {(activeWorkout.day_name || activeWorkout.name || '').toUpperCase()}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {activeRoutine?.id && (
                <RoutineShareMenu routine={activeRoutine}>
                  <Button variant="outline" size="sm" tooltip={t('tooltips.share', 'Share')} data-testid="workout-page-share-btn">
                    <Share2 className="h-4 w-4" />
                  </Button>
                </RoutineShareMenu>
              )}
              <Button variant="outline" size="sm" onClick={() => setReorderMode(true)} data-testid="reorder-exercises-btn" title="Reorder exercises">
                <GripVertical className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowDiscardConfirm(true)} data-testid="cancel-workout-btn">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {/* XP-style progress bar with stats inside */}
          {/* Background is dark slate so the white stats text + drop-shadow stays readable on every viewport, every theme, and on the unfilled portion of the bar (where the orange gradient hasn't reached yet). Previously bg-muted/50 = light-grey-on-light-page made the white text fail AA contrast on mobile. */}
          <div className="relative mt-2.5 h-8 rounded-lg overflow-hidden bg-zinc-800 border border-border/30" data-testid="workout-xp-bar">
            <div
              className="absolute inset-y-0 left-0 rounded-lg transition-all duration-500 ease-out"
              style={{
                width: `${progress}%`,
                background: 'linear-gradient(90deg, #FF5C00, #FF8C42)',
                boxShadow: progress > 0 ? '0 0 12px rgba(255,92,0,0.3)' : 'none',
              }}
            />
            <div className="absolute inset-0 flex items-center justify-between px-3">
              <span className="text-[11px] font-bold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.03em' }}>
                {completedCount}/{totalSets} {t('workout.setsCompletedCount')}
              </span>
              <span className="flex items-center gap-1 text-[11px] font-bold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" style={{ fontFamily: 'monospace' }} data-testid="workout-timer">
                <Timer className="h-3 w-3" />
                <WorkoutTimer startedAt={startedAt} isRunning={isTimerRunning} />
              </span>
              <span className="text-[11px] font-bold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.03em' }}>
                {totalCompletedVolumeKg.toLocaleString()} KG
              </span>
            </div>
          </div>
        </div>


        {/* Infinite scrollable exercise list */}
        {reorderMode ? (
          /* ── REORDER MODE: Hevy-style collapsed exercise list ── */
          <div className="mt-5" data-testid="reorder-mode-container">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-bold text-primary" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {t('workout.reorderExercises')}
              </p>
              <button
                onClick={() => setReorderMode(false)}
                className="px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-medium hover:bg-primary/90 transition-colors"
                data-testid="done-reorder-btn"
              >
                {t('workout.doneBtn')}
              </button>
            </div>
            <DndContext
              sensors={reorderSensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => {
                const { active, over } = event;
                reorderExercises(active.id, over?.id);
              }}
            >
              <SortableContext items={workoutData.map((exercise) => exercise.live_id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {workoutData.map((exercise, exIdx) => (
                    <SortableExerciseItem
                      key={exercise.live_id}
                      id={exercise.live_id}
                      exercise={exercise}
                      index={exIdx}
                      isFirst={exIdx === 0}
                      isLast={exIdx === workoutData.length - 1}
                      onMoveUp={() => reorderExercises(exercise.live_id, workoutData[exIdx - 1]?.live_id)}
                      onMoveDown={() => reorderExercises(exercise.live_id, workoutData[exIdx + 1]?.live_id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        ) : (
        <div className="space-y-6 mt-5">
          {workoutData.map((exercise, exIdx) => {
            // Superset label is shown only on the first exercise in a group.
            const supersetGroupId = supersetGroups[exIdx];
            let supersetLabel = null;
            if (supersetGroupId !== undefined) {
              const groupIndices = Object.entries(supersetGroups).filter(([, g]) => g === supersetGroupId).map(([i]) => parseInt(i)).sort((a, b) => a - b);
              const posInGroup = groupIndices.indexOf(exIdx);
              if (posInGroup === 0) {
                supersetLabel = t('workout.supersetLabel', { count: groupIndices.length });
              }
            }
            // Divider between exercises (but not between superset pairs).
            const isLast = exIdx >= workoutData.length - 1
              || (supersetGroups[exIdx] !== undefined && supersetGroups[exIdx + 1] !== undefined && supersetGroups[exIdx] === supersetGroups[exIdx + 1]);
            const stepKg = progressionSteps[exercise.name] || workoutPrefs.autoProgressStep;
            return (
              <ExerciseRow
                key={exercise.live_id || `${exercise.exercise_id}-${exIdx}`}
                exIdx={exIdx}
                exercise={exercise}
                isLast={isLast}
                supersetGroupId={supersetGroupId}
                supersetLabel={supersetLabel}
                setContextMenuState={setContextMenu}
                exerciseContextMenuState={exerciseContextMenu}
                cuesMap={cuesMap}
                exerciseImages={exerciseImages}
                sessionNoteValue={sessionNotes[exIdx] || ''}
                onSessionNoteChange={handleSessionNoteChange}
                progressionStep={stepKg}
                onProgressionStepChange={updateProgressionStep}
                liveActions={liveActions}
              />
            );
          })}

          {/* Empty state */}
          {workoutData.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Dumbbell className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">{t('workout.noExercises')}</p>
            </div>
          )}

          {/* Add Exercise — always right under last exercise or empty state */}
          {!showCompletionDialog && (
            <Button
              variant="outline"
              className="w-full h-11 text-sm mt-4"
              onClick={() => { setShowAddExercise(true); }}
              data-testid="add-exercise-btn"
            >
              <Plus className="mr-1 h-4 w-4" /> {t('workout.addExercise')}
            </Button>
          )}
        </div>
        )}
        {/* Finish — alone at the bottom */}
        {!showCompletionDialog && (
        <div className="mt-6">
          <Button
            onClick={finishWorkout}
            className="w-full h-12 text-sm bg-green-600 hover:bg-green-700 text-white"
            data-testid="finish-workout-btn"
            data-tour-anchor={TA.WORKOUT_FINISH_BTN}
          >
            <Trophy className="mr-1 h-4 w-4" /> {t('workout.finishWorkout')}
          </Button>
        </div>
        )}

        {/* Add Exercise - Hevy-style picker */}
        <ExercisePicker
          open={showAddExercise}
          onClose={() => setShowAddExercise(false)}
          onSelect={addAdhocExercise}
          title={t('workout.adhoc.title')}
        />

        {/* Swap Exercise — reuses ExercisePicker */}
        <ExercisePicker
          open={showSwapModal}
          onClose={() => { setShowSwapModal(false); setSwapSearch(''); setSwapExerciseIndex(null); }}
          onSelect={(ex) => { confirmSwap(ex); }}
          title={t('workout.swapExercise')}
          replacingExercise={swapExerciseIndex !== null ? workoutData[swapExerciseIndex] : null}
        />

        {/* Exercise Detail Modal */}
        <ExerciseDetailModal
          exercise={exerciseDetail}
          open={showExerciseDetail}
          onClose={() => { setShowExerciseDetail(false); exerciseDialog.close(); }}
        />

        {/* Plate Calculator Dialog */}
        <Dialog open={plateCalcDialog.open} onOpenChange={(open) => setPlateCalcDialog(prev => ({ ...prev, open }))}>
          <DialogContent className="max-w-sm" data-testid="plate-calc-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                <Weight className="h-5 w-5 text-orange-500" />
                {t('workout.ctxPlateCalc')}
                {plateCalcDialog.exerciseName && (
                  <span className="text-xs text-muted-foreground font-normal ml-auto truncate">{plateCalcDialog.exerciseName}</span>
                )}
              </DialogTitle>
            </DialogHeader>
            <PlateCalculator weight={plateCalcDialog.weight} />
          </DialogContent>
        </Dialog>

        {/* Unrealistic reps/weight sanity check — protects against pocket-entry */}
        <Dialog
          open={unrealisticConfirmDialog.open}
          onOpenChange={(open) => {
            if (!open) setUnrealisticConfirmDialog(d => ({ ...d, open: false }));
          }}
        >
          <DialogContent className="max-w-sm mx-auto">
            <DialogHeader>
              <DialogTitle>{t('workout.unrealisticCheck.title')}</DialogTitle>
            </DialogHeader>
            <div className="py-2 text-sm text-muted-foreground">
              {unrealisticConfirmDialog.flags.reps && unrealisticConfirmDialog.flags.weight
                ? t('workout.unrealisticCheck.bodyBoth', {
                    reps: unrealisticConfirmDialog.reps,
                    weight: unrealisticConfirmDialog.weight,
                    exerciseName: unrealisticConfirmDialog.exerciseName,
                  })
                : unrealisticConfirmDialog.flags.reps
                  ? t('workout.unrealisticCheck.bodyReps', {
                      reps: unrealisticConfirmDialog.reps,
                      exerciseName: unrealisticConfirmDialog.exerciseName,
                    })
                  : t('workout.unrealisticCheck.bodyWeight', {
                      weight: unrealisticConfirmDialog.weight,
                      exerciseName: unrealisticConfirmDialog.exerciseName,
                    })}
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <Button
                onClick={() => {
                  const { exerciseIndex, setIndex } = unrealisticConfirmDialog;
                  setUnrealisticConfirmDialog(d => ({ ...d, open: false }));
                  completeSet(exerciseIndex, setIndex, { bypassUnrealisticCheck: true });
                }}
                className="w-full"
              >
                {t('workout.unrealisticCheck.confirm')}
              </Button>
              <Button
                variant="outline"
                onClick={() => setUnrealisticConfirmDialog(d => ({ ...d, open: false }))}
                className="w-full"
              >
                {t('workout.unrealisticCheck.edit')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Warm-up Calculator Dialog */}
        <WarmUpCalculator
          open={warmupDialog.open}
          onClose={() => setWarmupDialog(prev => ({ ...prev, open: false }))}
          initialWeight={warmupDialog.weight}
          initialReps={warmupDialog.reps}
          exercise={warmupDialog.exercise || {}}
          units="kg"
        />

        {/* Share Badge Dialog — triggered from BadgeCelebration's Share button */}
        <MilestoneCardGenerator
          open={shareBadgeDialog.open}
          onClose={() => setShareBadgeDialog({ open: false, badge: null })}
          badge={shareBadgeDialog.badge}
          userName={user?.name || ''}
        />

        {/* Remove Exercise Confirm Dialog */}
        <Dialog open={showRemoveConfirm} onOpenChange={(open) => { setShowRemoveConfirm(open); if (!open) setRemoveExerciseIndex(null); }}>
          <DialogContent className="max-w-sm" data-testid="remove-exercise-dialog">
            <DialogHeader>
              <div className="mx-auto w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-2">
                <Trash2 className="h-6 w-6 text-red-500" />
              </div>
              <DialogTitle className="text-center" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {t('workout.removeExerciseTitle')}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground text-center py-2">
              {t('workout.removeExerciseBody', { name: removeExerciseIndex !== null ? workoutData[removeExerciseIndex]?.name : '' })}
            </p>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowRemoveConfirm(false); setRemoveExerciseIndex(null); }}
                data-testid="remove-exercise-cancel-btn"
              >
                {t('common.cancel')}
              </Button>
              <Button
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                onClick={() => removeExercise(removeExerciseIndex)}
                data-testid="remove-exercise-confirm-btn"
              >
                <Trash2 className="h-4 w-4 mr-1.5" /> {t('workout.removeExerciseConfirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Exercise context menu is now inline as a Popover on each exercise's 3-dot button */}
      </div>

      {/* Completion Dialog & related modals — rendered as a sibling so they
          persist across the activeWorkout=null transition mid-save. */}
      {completionDialogJSX}
    </>
  );
}
